/**
 * The compose sequence, as pure functions. THE PLAN, §4 P10.
 *
 * WHY THE STEPS LIVE HERE AND NOT IN THE PAGE. A broadcast is the one thing in this
 * product that costs the owner money per message and cannot be taken back, and the
 * sequence in front of it is six screens long. Two properties have to hold on every one of
 * them, and neither is something a template can be trusted with:
 *
 *  1. **A step you have not earned is not a step you can be on.** `resolveStep` is the
 *     whole guard: it takes the step the URL asked for and hands back the first one that
 *     is not yet complete. So `?step=check` typed into the address bar on an empty draft
 *     lands on "Name it", not on a Send button — which is exactly the difference between
 *     a sequence anchored on real routes and one anchored on DOM state that happens to
 *     have a URL painted on it.
 *  2. **A step that has nothing to ask is not shown.** A template with no blanks in it
 *     skips "Fill in the blanks" entirely, in the rail, in the guard and in the
 *     next/back arithmetic, because a step that renders an empty list and a Next button
 *     is a step that teaches the owner the sequence is padding.
 *
 * Every function here is total and side-effect free, so `specs/broadcasts.spec.js` can
 * walk the entire sequence — including the states that are unreachable by clicking —
 * without mounting anything.
 */

/** In order. The rail draws this, the guard walks it, and Next/Back index into it. */
export const BROADCAST_STEPS = [
  'name',
  'channel',
  'message',
  'fill',
  'audience',
  'check',
];

/** The three contact fields the sidecar's `bindings.ts` can actually read. */
export const CONTACT_FIELDS = ['name', 'phoneNumber', 'email'];

let rowSeq = 0;
/** Row identity for `v-for` keys. Not sent anywhere — `draftToWire` strips it. */
const nextRowId = () => {
  rowSeq += 1;
  return rowSeq;
};

export const audienceRow = (row = {}) => ({
  id: nextRowId(),
  field: row.field ?? '',
  operator: row.operator ?? '',
  value: row.value ?? '',
});

export const blankDraft = () => ({
  title: '',
  channelId: null,
  templateName: '',
  templateLanguage: '',
  /** `{ [variable]: { src, key } | { src: 'literal', value } }`. */
  bindings: {},
  /** Always at least one row, so the audience step opens with something to fill in. */
  audience: [audienceRow()],
});

/**
 * The bindings a template needs, seeded from what the draft already holds.
 *
 * Seeding matters on the way BACK: an owner who reaches the audience step, returns to the
 * message step and re-picks the SAME template must not lose the blanks she filled in.
 * Choosing a different template drops the bindings that named variables it does not have,
 * which is the same set-equality rule the BFF refuses on — a stale binding riding into a
 * draft is what burns it.
 */
export const bindingsForTemplate = (template, existing = {}) => {
  if (!template) return {};
  const out = {};
  template.variables.forEach(variable => {
    const held = existing[variable];
    // P-4.3: a title's blank or a button's value has no sensible contact default (a link's ending
    // is not a name), so it starts as words to write, which the "fill" step will not let past empty.
    const reserved =
      variable.startsWith('header:') || variable.startsWith('button:');
    out[variable] =
      held ??
      (reserved
        ? { src: 'literal', value: '' }
        : { src: 'field', key: 'name' });
  });
  return out;
};

const bindingIsComplete = binding => {
  if (!binding) return false;
  if (binding.src === 'literal')
    return String(binding.value ?? '').trim() !== '';
  return String(binding.key ?? '').trim() !== '';
};

const isPositiveInt = value => Number.isInteger(value) && value > 0;

/**
 * Which steps this draft actually has.
 *
 * `template` is the chosen template's DTO, or null while none is chosen. Null keeps "Fill
 * in the blanks" in the list: at that point we do not know whether the message has blanks,
 * and dropping a step we might have to put back would make the rail's length change under
 * the owner's cursor.
 */
export const stepsFor = template => {
  if (template && template.variables.length === 0) {
    return BROADCAST_STEPS.filter(step => step !== 'fill');
  }
  return [...BROADCAST_STEPS];
};

/**
 * Is this one step finished?
 *
 * `check` is never "complete" — it is the step where the send happens, so there is nothing
 * after it to unlock and nothing here to say yes to.
 */
export const isStepComplete = (step, draft, template) => {
  switch (step) {
    case 'name':
      return draft.title.trim() !== '';
    case 'channel':
      return isPositiveInt(draft.channelId);
    case 'message':
      return draft.templateName !== '' && draft.templateLanguage !== '';
    case 'fill':
      if (!template) return false;
      return template.variables.every(variable =>
        bindingIsComplete(draft.bindings[variable])
      );
    case 'audience':
      return draft.audience.some(
        row => row.field !== '' && row.operator !== ''
      );
    default:
      return false;
  }
};

/**
 * The step the page may actually render, given the one the URL asked for.
 *
 * Walks forward from the first step and stops at the first incomplete one, so a deep link
 * can only ever land EARLIER than it asked, never later. An unknown step name is treated
 * as "the furthest you have earned" rather than as an error — a stale bookmark from before
 * a step was renamed should open the sequence, not a 404.
 */
export const resolveStep = (requested, draft, template) => {
  const steps = stepsFor(template);
  const wantedAt = steps.indexOf(requested);
  const target = wantedAt === -1 ? steps.length - 1 : wantedAt;
  for (let at = 0; at < target; at += 1) {
    const step = steps[at];
    if (!isStepComplete(step, draft, template)) return step;
  }
  return steps[target];
};

/** The next / previous step, or null at either end. */
export const stepAfter = (step, template) => {
  const steps = stepsFor(template);
  const at = steps.indexOf(step);
  return at === -1 || at === steps.length - 1 ? null : steps[at + 1];
};

export const stepBefore = (step, template) => {
  const steps = stepsFor(template);
  const at = steps.indexOf(step);
  return at <= 0 ? null : steps[at - 1];
};

/**
 * The body the BFF's compose routes want.
 *
 * ROW IDS AND EMPTY ROWS ARE DROPPED HERE, and that is load-bearing rather than tidy: the
 * fingerprint the send is gated on is computed from the PARSED state, so a draft that
 * grew and then lost a blank row must serialise identically to one that never had it —
 * otherwise pressing Send after adding and removing a condition would be refused with
 * "this form changed after you previewed it" for a form that did not change.
 *
 * A row carrying a VALUE but no field is deliberately kept: dropping it would silently
 * widen the audience, which is the one direction this whole feature must never fail in.
 * The BFF refuses it by name.
 */
export const draftToWire = draft => ({
  title: draft.title.trim(),
  channelId: draft.channelId,
  templateName: draft.templateName,
  templateLanguage: draft.templateLanguage,
  bindings: Object.fromEntries(
    Object.entries(draft.bindings).map(([variable, binding]) =>
      binding.src === 'literal'
        ? [
            variable,
            { src: 'literal', value: String(binding.value ?? '').trim() },
          ]
        : [variable, { src: binding.src, key: binding.key }]
    )
  ),
  audience: draft.audience
    .filter(row => row.field !== '' || row.value.trim() !== '')
    .map(row => ({
      field: row.field,
      operator: row.operator,
      value: row.value.trim(),
    })),
});

/**
 * Everything that would be sent, as one string, with each blank replaced by what it will
 * be filled from.
 *
 * GUILLEMETS, NOT CURLY QUOTES, and for the reason written out in the Tasks example: the
 * substituted message is usually Arabic, `«»` are MIRRORED characters that the bidi
 * algorithm turns the right way round inside an RTL run, and `“”` are not — they render
 * as a closing quote opening the sentence.
 *
 * Global, because a template may repeat a variable and replacing only the first would show
 * the owner a message that differs from the one the sidecar renders.
 *
 * @param {string} body The approved template text, `{{1}}` placeholders included.
 * @param {object} bindings The draft's bindings.
 * @param {(binding: object|undefined) => string} labelFor Names one binding, in the
 *   owner's language. `?` for a blank with no binding at all — defensive, and the only
 *   honest thing to show if the template's variables ever stop matching.
 */
export const renderTemplateBody = (body, bindings, labelFor) =>
  String(body ?? '').replace(
    /\{\{([^}]+)\}\}/g,
    (match, name) => `«${labelFor(bindings[name.trim()])}»`
  );

export default BROADCAST_STEPS;
